import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import { toCanvas } from 'html-to-image';

import { AppSharedModule } from 'app/app-shared.module';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { AiChartComponent, ChartData } from '../ai-chart/ai-chart.component';
import { AiComparisonComponent } from '../ai-comparison/ai-comparison.component';
import { AiDocumentCardsComponent } from '../ai-document-cards/ai-document-cards.component';
import { AiDriversComponent } from '../ai-drivers/ai-drivers.component';
import { AiMetricsComponent } from '../ai-metrics/ai-metrics.component';
import { AiSmartSummaryComponent } from '../ai-smart-summary/ai-smart-summary.component';
import { AiSuggestActionsComponent } from '../ai-suggest-actions/ai-suggest-actions.component';
import { AiTableComponent } from '../ai-table/ai-table.component';
import {
  ComparisonData,
  DocumentsData,
  DriversData,
  MetricCard,
  SmartSummaryData,
  SuggestActionsData,
  TableData,
  ToolContent,
} from '../tool-content.model';

interface AssistantMessage {
  readonly role: 'user' | 'assistant' | 'tool';
  readonly content?: SafeHtml;
  readonly text: string;
  readonly chartData?: ChartData;
  readonly chartStatus?: 'loading' | 'error';
  readonly chartToolName?: string;
  readonly toolContent?: ToolContent;
  readonly toolContentStatus?: 'loading' | 'error';
  readonly toolContentToolName?: string;
}

/**
 * Renders assistant chat messages and handles message-level actions.
 */
@Component({
  selector: 'app-spotlight-assistant-message-list',
  templateUrl: './spotlight-assistant-message-list.component.html',
  imports: [
    AppSharedModule,
    AiChartComponent,
    AiComparisonComponent,
    AiTableComponent,
    AiMetricsComponent,
    AiDocumentCardsComponent,
    AiDriversComponent,
    AiSmartSummaryComponent,
    AiSuggestActionsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpotlightAssistantMessageListComponent {
  private readonly alertService = inject(AlertService);
  private readonly translocoService = inject(TranslocoService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly logger = inject(LoggerService);

  readonly messages = input.required<AssistantMessage[]>();
  readonly isWaitingForResponse = input<boolean>(false);
  readonly actionPromptRequested = output<string>();

  protected readonly activeMenuMessage = signal<AssistantMessage | null>(null);
  protected readonly activeMenuMessageIndex = signal<number | null>(null);

  protected openMessageMenu(message: AssistantMessage, event: Event, messageIndex: number): void {
    event.stopPropagation();
    this.activeMenuMessage.set(message);
    this.activeMenuMessageIndex.set(messageIndex);
  }

  protected clearActiveMenuMessage(): void {
    this.activeMenuMessage.set(null);
    this.activeMenuMessageIndex.set(null);
  }

  protected shouldShowMessageActions(message: AssistantMessage): boolean {
    if (message.role !== 'assistant') {
      return false;
    }
    if (message.chartStatus === 'loading') {
      return false;
    }
    if (message.toolContentStatus === 'loading') {
      return false;
    }
    const hasText = typeof message.text === 'string' && message.text.trim().length > 0;
    return hasText || !!message.chartData || !!message.toolContent;
  }

  protected getLoadingStatusKey(message: AssistantMessage): string {
    if (message.chartStatus === 'loading' || message.chartToolName === 'displayChart') {
      return 'status-generating-chart';
    }
    switch (message.toolContentToolName) {
      case 'displayDocuments':
        return 'status-retrieving-documents';
      case 'displayMetrics':
        return 'status-calculating-metrics';
      case 'displayTable':
        return 'status-building-table';
      case 'displaySmartSummary':
        return 'status-analyzing-themes';
      case 'displayComparison':
        return 'status-comparing-segments';
      case 'suggestActions':
        return 'status-drafting-recommendations';
      case 'displayDrivers':
        return 'status-analyzing-drivers';
      default:
        return 'status-analyzing-data';
    }
  }

  protected async copyActiveMessageToClipboard(): Promise<void> {
    const message = this.activeMenuMessage();
    if (!message) {
      return;
    }
    const text = this.buildClipboardText(message);
    if (!text) {
      return;
    }
    if (message.chartData) {
      const copied = await this.copyChartImageWithText(text);
      if (copied) {
        this.alertService.showToast(
          this.translocoService.translate('ai-assistant.chart-image-copied')
        );
        return;
      }
    }
    const copied = await this.copyToClipboard(text);
    if (copied) {
      this.alertService.showToast(
        this.translocoService.translate(
          'account-dropdown-wrapper.text-has-been-copied-to-clipboard'
        )
      );
    }
  }

  protected async copyChartValuesToClipboard(
    message: AssistantMessage,
    event: Event
  ): Promise<void> {
    event.stopPropagation();
    if (!message.chartData) {
      return;
    }
    const chartText = this.buildChartClipboardText(message.chartData).trim();
    if (!chartText) {
      return;
    }
    const copied = await this.copyToClipboard(chartText);
    if (copied) {
      this.alertService.showToast(
        this.translocoService.translate(
          'account-dropdown-wrapper.text-has-been-copied-to-clipboard'
        )
      );
    }
  }

  private async copyChartImageWithText(text: string): Promise<boolean> {
    const chartElement = this.findActiveChartElement();
    if (!chartElement) {
      return false;
    }
    if (!navigator.clipboard || typeof navigator.clipboard.write !== 'function') {
      return false;
    }
    if (typeof ClipboardItem !== 'function') {
      return false;
    }
    const canvas = await this.renderChartCanvas(chartElement);
    if (!canvas) {
      return false;
    }
    const pngBlob = await this.canvasToPngBlob(canvas);
    if (!pngBlob) {
      return false;
    }
    const textBlob = new Blob([text], { type: 'text/plain' });
    await navigator.clipboard.write([
      new ClipboardItem({
        [pngBlob.type]: pngBlob,
        [textBlob.type]: textBlob,
      }),
    ]);
    return true;
  }

  private async renderChartCanvas(chartElement: HTMLElement): Promise<HTMLCanvasElement | null> {
    try {
      return await toCanvas(chartElement, { backgroundColor: '#ffffff' });
    } catch (error) {
      this.logger.error('Failed to render chart image for clipboard', { error });
      return null;
    }
  }

  private findActiveChartElement(): HTMLElement | null {
    const index = this.activeMenuMessageIndex();
    if (index === null) {
      return null;
    }
    const selector = `[data-message-index="${index}"] .ai-chart`;
    return this.elementRef.nativeElement.querySelector(selector) as HTMLElement | null;
  }

  private canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
  }

  private buildClipboardText(message: AssistantMessage): string | null {
    const rawText = message.text ?? '';
    const trimmedText = rawText.trim();
    const chartText = message.chartData ? this.buildChartClipboardText(message.chartData) : '';
    const toolContentText = message.toolContent
      ? this.buildToolContentClipboardText(message.toolContent)
      : '';
    const combined = [trimmedText, chartText, toolContentText]
      .filter((value) => value.trim().length > 0)
      .join('\n\n');
    const trimmedCombined = combined.trim();
    if (!trimmedCombined) {
      return null;
    }
    return trimmedCombined;
  }

  private buildToolContentClipboardText(toolContent: ToolContent): string {
    switch (toolContent.type) {
      case 'table': {
        const { columns, rows } = toolContent.data as TableData;
        if (!columns?.length || !rows?.length) {
          return '';
        }
        const header = columns.map((c) => c.label).join('\t');
        const maxRows = 20;
        const visibleRows = rows.slice(0, maxRows);
        const body = visibleRows
          .map((row) => columns.map((c) => String(row[c.key] ?? '')).join('\t'))
          .join('\n');
        const suffix =
          rows.length > maxRows
            ? `\n… (${this.translocoService.translate('ai-assistant.clipboard-more-rows', {
                count: rows.length - maxRows,
              })})`
            : '';
        return `${header}\n${body}${suffix}`;
      }
      case 'metrics': {
        const metrics = toolContent.data as MetricCard[];
        if (!metrics?.length) {
          return '';
        }
        return metrics
          .map((m) => {
            const trend = m.trend
              ? ` (${this.translocoService.translate(`ai-assistant.clipboard-trend-${m.trend}`)})`
              : '';
            const desc = m.description ? ` — ${m.description}` : '';
            return `${m.label}: ${m.value}${trend}${desc}`;
          })
          .join('\n');
      }
      case 'documents': {
        const docs = toolContent.data as DocumentsData;
        if (!docs?.documents?.length) {
          return '';
        }
        const lines: string[] = [];
        if (docs.title) {
          lines.push(docs.title);
        }
        for (const doc of docs.documents) {
          const parts: string[] = [];
          if (doc.sentiment) {
            parts.push(`[${doc.sentiment}]`);
          }
          parts.push(doc.text);
          lines.push(parts.join(' '));
        }
        if (docs.totalMatched > docs.documents.length) {
          lines.push(
            `… (${this.translocoService.translate('ai-assistant.clipboard-more-items', {
              count: docs.totalMatched - docs.documents.length,
            })})`
          );
        }
        return lines.join('\n\n');
      }
      case 'smartSummary': {
        const summary = toolContent.data as SmartSummaryData;
        if (!summary) {
          return '';
        }
        const lines: string[] = [];
        if (summary.title) {
          lines.push(summary.title);
        }
        if (summary.summary) {
          lines.push(summary.summary);
        }
        if (summary.keyFindings?.length) {
          lines.push(summary.keyFindings.map((f) => `• ${f}`).join('\n'));
        }
        if (summary.verbatimExamples?.length) {
          lines.push(
            summary.verbatimExamples
              .map((v) => {
                const label = v.sentiment ? `[${v.sentiment}] ` : '';
                return `${label}"${v.text}"`;
              })
              .join('\n')
          );
        }
        return lines.join('\n\n');
      }
      case 'comparison': {
        const comparison = toolContent.data as ComparisonData;
        if (!comparison?.deltas?.length) {
          return '';
        }
        const lines: string[] = [];
        if (comparison.title) {
          lines.push(comparison.title);
        }
        const header = `${this.translocoService.translate('ai-assistant.comparison-metric')}\t${comparison.sliceA.label}\t${comparison.sliceB.label}\t${this.translocoService.translate('ai-assistant.comparison-change')}`;
        const body = comparison.deltas
          .map((d) => {
            const sign = d.delta > 0 ? '+' : '';
            return `${d.metric}\t${d.sliceA}\t${d.sliceB}\t${sign}${d.delta}`;
          })
          .join('\n');
        lines.push(`${header}\n${body}`);
        return lines.join('\n\n');
      }
      case 'suggestActions': {
        const suggest = toolContent.data as SuggestActionsData;
        if (!suggest?.actions?.length) {
          return '';
        }
        const lines: string[] = [];
        if (suggest.title) {
          lines.push(suggest.title);
        }
        lines.push(suggest.actions.map((a) => `• ${a.label}: ${a.description}`).join('\n'));
        return lines.join('\n\n');
      }
      case 'drivers': {
        const driversResult = toolContent.data as DriversData;
        if (!driversResult?.drivers?.length) {
          return '';
        }
        const lines: string[] = [];
        if (driversResult.title) {
          lines.push(driversResult.title);
        }
        if (driversResult.target) {
          lines.push(
            this.translocoService.translate('ai-assistant.drivers-target', {
              target: driversResult.target,
            })
          );
        }
        lines.push(
          driversResult.drivers
            .map((d) => {
              const direction = this.translocoService.translate(
                `ai-assistant.clipboard-direction-${d.direction}`
              );
              return `• ${d.driver} (${(d.importance * 100).toFixed(0)}%, ${direction}): ${d.explanation}`;
            })
            .join('\n')
        );
        return lines.join('\n\n');
      }
      default:
        return '';
    }
  }

  private buildChartClipboardText(chartData: ChartData): string {
    const config = chartData.config;
    const lines: string[] = [];
    if (config.title) {
      lines.push(config.title);
    }
    if (config.description) {
      lines.push(config.description);
    }
    if (config.type) {
      lines.push(
        this.translocoService.translate('ai-assistant.clipboard-chart-type', {
          type: config.type,
        })
      );
    }
    if (config.takeaway) {
      lines.push(
        this.translocoService.translate('ai-assistant.clipboard-takeaway', {
          takeaway: config.takeaway,
        })
      );
    }
    const tableText = this.buildChartTableText(chartData.data);
    if (tableText) {
      lines.push(tableText);
    }
    return lines.join('\n');
  }

  private buildChartTableText(rows: Record<string, unknown>[]): string | null {
    if (!Array.isArray(rows) || rows.length === 0) {
      return null;
    }
    const keys = Object.keys(rows[0] ?? {});
    if (keys.length === 0) {
      return null;
    }
    const normalizeCell = (value: unknown): string => {
      if (value == null) {
        return '';
      }
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
      return JSON.stringify(value);
    };
    const maxRows = 20;
    const visibleRows = rows.slice(0, maxRows);
    const header = keys.join('\t');
    const body = visibleRows
      .map((row) => keys.map((key) => normalizeCell(row[key])).join('\t'))
      .join('\n');
    const truncatedSuffix =
      rows.length > maxRows
        ? `\n… (${this.translocoService.translate('ai-assistant.clipboard-more-rows', {
            count: rows.length - maxRows,
          })})`
        : '';
    return `${this.translocoService.translate('ai-assistant.clipboard-data')}:\n${header}\n${body}${truncatedSuffix}`;
  }

  private async copyToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.left = '0';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  }
}
